import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationHookOptions,
  MutationResult,
  useMutation,
} from '@apollo/client';

import {
  evictCache,
  RdProjectEligibilityAnswersInput,
  UPDATE_RD_PROJECT_ELIGIBILITY,
  UpdateRdProjectEligibilityMutation,
  UpdateRdProjectEligibilityMutationVariables,
} from '@neo.tax/shared';

type UpdateRdProjectEligibilityOptions = MutationHookOptions<
  NoInfer<UpdateRdProjectEligibilityMutation>,
  NoInfer<UpdateRdProjectEligibilityMutationVariables>,
  DefaultContext
>;

type UseUpdateRdProjectEligibility = [
  (
    rdProjectId: string,
    rdProjectEligibilityAnswersInput: RdProjectEligibilityAnswersInput,
    options?: UpdateRdProjectEligibilityOptions,
  ) => Promise<FetchResult<UpdateRdProjectEligibilityMutation>>,
  MutationResult<UpdateRdProjectEligibilityMutation>,
];

const UPDATE_RD_PROJECT_ELIGIBILITY_REQUEST_KEY = 'UPDATE_RD_PROJECT_ELIGIBILITY_REQUEST_KEY';

export const useUpdateRdProjectEligibility = (): UseUpdateRdProjectEligibility => {
  const [updateRdProjectEligibilityMutation, updateRdProjectEligibilityMutationResult] = useMutation<
    UpdateRdProjectEligibilityMutation,
    UpdateRdProjectEligibilityMutationVariables
  >(UPDATE_RD_PROJECT_ELIGIBILITY, {
    context: {
      serializationKey: UPDATE_RD_PROJECT_ELIGIBILITY_REQUEST_KEY,
      debounceKey: UPDATE_RD_PROJECT_ELIGIBILITY_REQUEST_KEY,
      errorToast: {
        title: 'Error processing R&D project eligibility',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
        duration: 5000,
      },
    },
  });

  const updateRdProjectEligibility = (
    rdProjectId: string,
    rdProjectEligibilityAnswersInput: RdProjectEligibilityAnswersInput,
    options: UpdateRdProjectEligibilityOptions = {},
  ): Promise<FetchResult<UpdateRdProjectEligibilityMutation>> =>
    updateRdProjectEligibilityMutation({
      variables: {
        rdProjectId,
        rdProjectEligibilityAnswersInput,
      },
      update: (cache: ApolloCache<UpdateRdProjectEligibilityMutation>): void => {
        //invalidate RdProject cache
        evictCache(cache, ['getRdProjects']);
      },
      ...options,
    });

  return [updateRdProjectEligibility, updateRdProjectEligibilityMutationResult];
};
